import { ReactComponent as DownArrowSvg } from '@assets/icons/arrow-down.svg';
import { Menu, Popover, Tooltip } from 'antd';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { VideoJsPlayer } from 'video.js';
import ControlBarCurrentTime from '@pages/MediaViewer/AssetViewer/VideoCard/components/ControlBar/ControlBarCurrentTime';
import {
  getFrameAtTime,
  getSmpteTimecodeAtTime,
  getVideoFrameRate,
  isAudioVideo
} from '@helpers/avInfo';
import { formatDuration } from '@helpers/formatDuration';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { LgWidth } from '@components/Responsive';

type ControlBarTimerProps = {
  player: VideoJsPlayer;
  duration: number;
};

export default memo(function ControlBarTimer({
  player,
  duration
}: ControlBarTimerProps) {
  const [timeFormatVisible, setTimeFormatVisible] = useState<boolean>(false);
  const {
    playerTime,
    isTimecodeChecked,
    canvasMode,
    timeFormat,
    selectedCommentId,
    selectedVersion,
    setPlayerTime,
    setTimeFormat
  } = useAssetViewerState();

  const videoFrameRate = useMemo(() => {
    if (!selectedVersion || !isAudioVideo(selectedVersion.metadata)) {
      return undefined;
    }
    return getVideoFrameRate(selectedVersion.metadata);
  }, [selectedVersion]);

  const renderTime = useCallback(
    (time: number) => {
      const metadata = selectedVersion?.metadata;
      const frame = getFrameAtTime(time, metadata);
      const videoFrames = isAudioVideo(metadata)
        ? metadata.info.avInfo?.video?.frames
        : undefined;
      const avTimecode = isAudioVideo(metadata)
        ? metadata.info.avInfo?.timecode
        : undefined;
      const timecode = getSmpteTimecodeAtTime(time, avTimecode, videoFrames);

      if (timeFormat === 'frames' && frame !== null) {
        return String(frame);
      }
      if (timeFormat === 'timecode' && timecode !== null) {
        return String(timecode);
      }

      return formatDuration(time);
    },
    [timeFormat, selectedVersion?.metadata]
  );

  return (
    <div className="video_control_bar_container__time">
      {(canvasMode === 'edit' ||
        selectedCommentId === 'new' ||
        selectedCommentId === 'reply') &&
      playerTime[1] &&
      playerTime[1] >= 0 &&
      isTimecodeChecked ? (
        <div
          className="clear_playback_box"
          onClick={() => {
            if (
              (canvasMode === 'edit' ||
                selectedCommentId === 'new' ||
                selectedCommentId === 'reply') &&
              playerTime[1] &&
              playerTime[1] >= 0
            ) {
              setPlayerTime([playerTime[0], null]);
            }
          }}
        >
          <span className="copy">Clear playback range</span>
          <LgWidth>
            <span className="action">Esc</span>
          </LgWidth>
        </div>
      ) : (
        <Popover
          placement="topRight"
          trigger={['click']}
          getPopupContainer={() =>
            document.querySelector('.video-js') as HTMLElement
          }
          onOpenChange={(v) => setTimeFormatVisible(v)}
          open={timeFormatVisible}
          overlayStyle={{ width: 140 }}
          overlayClassName="control_bar_select_overlay"
          content={
            <Menu
              prefixCls="media_viewer_menu-menu"
              selectedKeys={[timeFormat]}
              onClick={(e) => {
                if (
                  (e.key === 'frames' || e.key === 'timecode') &&
                  !videoFrameRate
                )
                  return;
                setTimeFormatVisible(false);
                setTimeFormat(e.key as any);
              }}
              items={[
                { key: 'standard', label: 'Standard' },
                { key: 'frames', label: 'Frames', disabled: !videoFrameRate },
                {
                  key: 'timecode',
                  label: 'Timecode',
                  disabled: !videoFrameRate
                }
              ]}
            />
          }
        >
          <Tooltip
            title={
              timeFormat === 'standard'
                ? 'Switch to frame'
                : 'Switch to standard'
            }
            placement="bottom"
            overlayClassName="toggle_favorite_overlay"
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ControlBarCurrentTime player={player} formatTime={renderTime} />
              <div className="divider">/</div>
              <div className="total">{renderTime(duration)}</div>
              <DownArrowSvg />
            </div>
          </Tooltip>
        </Popover>
      )}
    </div>
  );
});
